import colors from "../data/colors"
import { scaleLinear } from "d3"
import { toFixed } from "../utils/helpers"
import { distribution_parenthood } from "../data/distribution-chart/parenthood"
import { distribution_language } from "../data/distribution-chart/language"
import { distribution_overall } from "../data/distribution-chart/overall"
import {
  highlightPass,
  highlightFail,
} from "../data/distribution-chart/annotations"

export const dimensions = {
  width: 600,
  height: 480,
  padding: {
    top: 0,
    bottom: 80,
    left: 20,
    right: 20,
  },
  radius: 3,
}

export const THRESHOLD_SCORE = 0.6970136
export const THRESHOLD_SCORE_ROUNDED = 0.7

// Labels in data
const CHILDREN = 1, // Bad score
  NO_CHILDREN = 0
const DUTCH = 1,
  NO_DUTCH = 0 // Bad score

// white fill, bad score
const getFillAndStrokeForOverall = (label) => {
  const fill = {
    0: "white",
  }
  const stroke = {
    0: "",
  }
  const strokeWidth = {
    0: 0,
  }
  return {
    fill: fill[label],
    stroke: stroke[label],
    strokeWidth: strokeWidth[label],
  }
}

// white fill, bad score
const getFillAndStrokeForParenthood = (label) => {
  const fill = {
    [CHILDREN]: colors.ageUnder35,
    [NO_CHILDREN]: colors.transparent,
  }
  const stroke = {
    [CHILDREN]: "",
    [NO_CHILDREN]: colors.ageUnder35,
  }
  const strokeWidth = {
    [CHILDREN]: 0,
    [NO_CHILDREN]: 1,
  }
  return {
    fill: fill[label],
    stroke: stroke[label],
    strokeWidth: strokeWidth[label],
  }
}

const getFillAndStrokeForLanguage = (label) => {
  const fill = {
    [NO_DUTCH]: colors.lightBlue,
    [DUTCH]: colors.transparent,
  }
  const stroke = {
    [NO_DUTCH]: "",
    [DUTCH]: colors.lightBlue,
  }
  const strokeWidth = {
    [NO_DUTCH]: 0,
    [DUTCH]: 1,
  }
  return {
    fill: fill[label],
    stroke: stroke[label],
    strokeWidth: strokeWidth[label],
  }
}

// Highlighting Sara and Yusuf
export const HIGHLIGHTS = {
  language: {
    saraDutch: "single_mom_lang_req1",
    saraNoDutch: "single_mom_lang_req0",
    yusufDutch: "migrant_man_lang_req1",
    yusufNoDutch: "migrant_man_lang_req0",
  },
  parenthood: {
    sara: "single_mom_default",
    yusuf: "migrant_man_default",
  },
  age: {
    sara30: "single_mom30",
    sara50: "single_mom50",
    sara70: "single_mom70",
    yusuf30: "migrant_man30",
    yusuf50: "migrant_man50",
    yusuf70: "migrant_man70",
  },
}

export const getFillAndStrokeForLanguageHighlight = (label) => {
  const mapping = {
    // [HIGHLIGHTS.language.saraDutch]: highlightPass.circle,
    // [HIGHLIGHTS.language.saraNoDutch]: highlightFail.circle,
    [HIGHLIGHTS.language.yusufDutch]: highlightPass.circleOutline,
    [HIGHLIGHTS.language.yusufNoDutch]: highlightFail.circle,
  }
  return mapping[label]
}

export const getFillAndStrokeForParenthoodHighlight = (label) => {
  const mapping = {
    [HIGHLIGHTS.parenthood.sara]: highlightFail.circle,
    // [HIGHLIGHTS.parenthood.yusuf]: highlightPass.circle,
  }
  return mapping[label]
}

const calculateNodePosition = (
  data,
  sortOrder,
  xScale,
  dimensions,
  getFillFx
) => {
  const {
    height,
    padding: { bottom: pBottom },
    radius,
  } = dimensions

  const scoreData = data.reduce((r, a) => {
    const score = toFixed(a["score"], 2)
    r[score] = r[score] || []
    r[score].push(a)
    return r
  }, Object.create(null))

  // Clubbing scores together that are same till 2 decimal precision
  const scoreWiseData = Object.entries(scoreData)
    .map((d) => {
      const sortedList = sortOrder.flatMap((label) =>
        d[1].filter((a) => a.label === label).sort((a, b) => a.score - b.score)
      )
      return [Number(d[0]), sortedList]
    })
    .sort((a, b) => a[0] - b[0])

  // Position for dots
  const nodes = scoreWiseData.flatMap((score) => {
    const scoreValue = Number(score[0])
    // Small right offset for values >= 0.7 to accomodate the threshold line
    let cx = scoreValue >= 0.7 ? xScale(scoreValue) + 3.5 : xScale(scoreValue)
    const newScore = score[1].map((d, j) => {
      let cy = height - pBottom * 1.1 - (j + 1) * (3 * radius)
      return {
        ...d,
        cx,
        cy,
        // scoreRounded: score[0],
        ...getFillFx(d.label),
        // opacity: 1,
      }
    })
    return newScore
  })

  return nodes
}

export const getChartNodes = () => {
  const SCORE_EXTENT = [0.15, 0.86]
  const {
    width,
    height,
    padding: { left: pLeft, right: pRight, bottom: pBottom },
  } = dimensions

  // Scales
  const xScale = scaleLinear()
    .domain(SCORE_EXTENT)
    .range([pLeft, width - pRight])

  // Nodes -------------------------------------------------------------
  const nodesOverall = calculateNodePosition(
    distribution_overall,
    [0, 1],
    xScale,
    dimensions,
    getFillAndStrokeForOverall
  )

  const nodesParenthood = calculateNodePosition(
    distribution_parenthood,
    [CHILDREN, NO_CHILDREN],
    xScale,
    dimensions,
    getFillAndStrokeForParenthood
  )
  const nodesLanguage = calculateNodePosition(
    distribution_language,
    [NO_DUTCH, DUTCH],
    xScale,
    dimensions,
    getFillAndStrokeForLanguage
  )
  const nodesLanguagePass = [],
    nodesLanguageFail = [],
    nodesParenthoodPass = [],
    nodesParenthoodFail = []
  nodesLanguage.forEach((d) => {
    if (d.label === DUTCH) nodesLanguagePass.push(d)
    else nodesLanguageFail.push(d)
  })
  nodesParenthood.forEach((d) => {
    if (d.label === NO_CHILDREN) nodesParenthoodPass.push(d)
    else nodesParenthoodFail.push(d)
  })

  // Legend
  const legendRadius = 6
  const leftConstant = width - pRight - legendRadius / 2
  const legendParenthood = [
    {
      name: "1+ CHILD",
      id: CHILDREN,
      textX: leftConstant - 132,
      textY: 2,
      r: 6,
      cx: leftConstant - 120,
      cy: 0,
      ...getFillAndStrokeForParenthood(CHILDREN),
    },
    {
      name: "NO CHILDREN",
      id: NO_CHILDREN,
      textX: leftConstant - 12,
      textY: 2,
      r: 6,
      cx: leftConstant,
      cy: 0,
      ...getFillAndStrokeForParenthood(NO_CHILDREN),
    },
  ]
  const legendLanguage = [
    {
      name: "FAILED",
      id: NO_DUTCH,
      textX: leftConstant - 92,
      textY: 2,
      r: 6,
      cx: leftConstant - 80,
      cy: 0,
      ...getFillAndStrokeForLanguage(NO_DUTCH),
    },
    {
      name: "PASSED",
      id: DUTCH,
      textX: leftConstant - 12,
      textY: 2,
      r: 6,
      cx: leftConstant,
      cy: 0,
      ...getFillAndStrokeForLanguage(DUTCH),
    },
  ]

  return {
    SCORE_EXTENT,
    xScale,
    nodesOverall,
    nodesParenthood,
    legendParenthood,
    nodesLanguage,
    legendLanguage,
    nodesLanguagePass,
    nodesLanguageFail,
    nodesParenthoodPass,
    nodesParenthoodFail,
  }
}
